import React, { useCallback } from 'react';
import { MDText } from 'i18n-react';

import { DetailsLinkVrmModalListTable } from 'Components/pages/VehicleDetailsRedesign/Components/DetailsLinkVrmModalListTable/DetailsLinkVrmModalListTable';
import type { ListTableValuesInterface } from 'Types/listTable';
import type { Vehicle } from 'Types/vehicle';
import dayjs from 'Utilities/dayjs';
import { formatVehicleName } from 'Utilities/formatters';

import LocalTexts from './KeeperDetailsField.texts';

import styles from './KeeperDetailsField.module.scss';

const LocalT = new MDText(LocalTexts);

interface KeeperDetailsFieldProps {
	events: {
		onModalClose: () => void;
		onModalOpen: () => void;
	};
	keepersList: Vehicle['keepersList'];
	make: Vehicle['spec']['make'];
	model: Vehicle['spec']['model'];
	prettyVrm: string;
	previousKeepersCount: Vehicle['previousKeepersCount'];
	vrm: Vehicle['vrm'];
}

export const KeeperDetailsField: React.FC<KeeperDetailsFieldProps> = ({
	events,
	keepersList,
	make,
	model,
	prettyVrm,
	previousKeepersCount,
	vrm: apiVrm,
}) => {
	const makeModel = formatVehicleName({ make, model });
	const vrm = prettyVrm || apiVrm;

	const modalText = {
		footer: <span className={styles.modalFooter}>{LocalT.translate('modal.footer')}</span>,
		subtitle: LocalT.translate('modal.subtitle', {
			count: previousKeepersCount,
			plural: previousKeepersCount !== 1 ? 's' : '',
		}),
		title: LocalT.translate('modal.title'),
	};

	const tableValues = useCallback(() => {
		if (!keepersList?.length) {
			return [];
		}

		const keepers = [...keepersList].map((keeper, index) => {
			const keeperStartDate = dayjs(keeper.startDate);
			const nextStartDate = dayjs(keepersList[index + 1]?.startDate || new Date());

			const getAgeDetails = (oldDate: dayjs.Dayjs, newDate: dayjs.Dayjs) => {
				const years = newDate.diff(oldDate, 'year');
				const months = newDate.diff(oldDate, 'month') - years * 12;
				const days = newDate.diff(oldDate.add(years, 'year').add(months, 'month'), 'day');

				return [{ years }, { months }, { days }].reduce((acc, value) => {
					const [k, v] = Object.entries(value)[0];

					if (v) {
						const pluralKey = v === 1 ? k.replace(/s$/, '') : k;
						acc.push(`${v} ${pluralKey}`);
					}
					return acc;
				}, [] as string[]);
			};

			const readableIndex = index + 1;

			return {
				key: index.toString(),
				label: readableIndex === keepersList.length ? LocalT.translate('modal.currentOwner') : readableIndex.toString(),
				value: getAgeDetails(keeperStartDate, nextStartDate)
					.join(', ')
					.replace(/,\s([^,]+)$/, ' and $1'),
			};
		});

		const tableHeader = Object.keys(LocalTexts.modal.table).reduce(
			(acc, key) => ({ ...acc, [key]: LocalT.translate(`modal.table.${key}`) }),
			{},
		) as ListTableValuesInterface;

		return [tableHeader, ...keepers];
	}, [keepersList]);

	const showDetails = !!(previousKeepersCount && keepersList?.length);

	return (
		<div className={styles.component}>
			{previousKeepersCount}
			{showDetails && (
				<DetailsLinkVrmModalListTable
					badgeClassName={styles.badge}
					modalClassName={styles.keeperDetailsModal}
					modalEvents={{
						onModalClose: events.onModalClose,
						onModalOpen: events.onModalOpen,
					}}
					values={tableValues()}
					{...{ makeModel, ...modalText, vrm }}
				/>
			)}
		</div>
	);
};
